import { z } from 'zod';
import { queryBoolean } from './query.js';
import { SportNameSchema, type SportName } from './profile.js';
import { LanguageCodeSchema, type TrainerSpecialty } from './trainer-profile.js';

/**
 * F33 — B2C trainer marketplace search contract (`GET /public/trainers`).
 * Anonymous-OK, cached 60s. Excludes deactivated/suspended/rejected trainers.
 * Sport is the only required filter; default sort = verified-first (D18).
 *
 * F09 v2 (E31 / §2.2): verified-first applies only to `recommended` and the
 * explicit `verified` sort (kept as a real option — mobile's default);
 * `verifiedOnly` means the F09 trainer tick; a rejected tick request no longer
 * delists a trainer (only suspended/deactivated are excluded).
 */

export const TRAINER_MARKETPLACE_SORT_VALUES = [
  'verified',
  'nearest',
  'recommended',
  'rating', // reserved inert until F37 reviews ship
] as const;
export type TrainerMarketplaceSort = (typeof TRAINER_MARKETPLACE_SORT_VALUES)[number];
export const TrainerMarketplaceSortSchema = z.enum(TRAINER_MARKETPLACE_SORT_VALUES);

export const TRAINER_SEARCH_DEFAULT_LIMIT = 20;
export const TRAINER_SEARCH_MAX_LIMIT = 50;

export const TrainerSearchFiltersSchema = z
  .object({
    sport: SportNameSchema, // required (D18)
    lat: z.coerce.number().min(-90).max(90).optional(),
    lng: z.coerce.number().min(-180).max(180).optional(),
    radiusKm: z.coerce.number().min(1).max(200).optional(),
    priceMin: z.coerce.number().int().min(0).optional(),
    priceMax: z.coerce.number().int().min(0).optional(),
    verifiedOnly: queryBoolean().optional(),
    language: LanguageCodeSchema.optional(),
    minSpecialtyLevel: z.coerce.number().min(1.0).max(7.0).optional(),
    sort: TrainerMarketplaceSortSchema.optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(TRAINER_SEARCH_MAX_LIMIT).optional(),
  })
  .strict()
  .refine((v) => v.sort !== 'nearest' || (v.lat !== undefined && v.lng !== undefined), {
    message: 'sort=nearest requires lat + lng',
    path: ['sort'],
  });
export type TrainerSearchFilters = z.infer<typeof TrainerSearchFiltersSchema>;

export type TrainerSearchResultItem = {
  readonly userId: string;
  readonly displayName: string;
  /**
   * F09 v2 (E32) — the trainer tick (`trainer_profiles.verification_status =
   * 'verified'`). F09 v2 removed the public `verificationStatus` status value.
   */
  readonly verified: boolean;
  /** Display-only "since" date. */
  readonly verifiedAt: string | null;
  readonly headline: string | null;
  readonly avatarUrl: string | null;
  readonly sports: readonly SportName[];
  readonly specialties: readonly TrainerSpecialty[];
  readonly languages: readonly string[];
  /** Min active-package per-session price (USD minor units), or null if none. */
  readonly minPriceAmount: number | null;
  readonly priceCurrency: string;
  /** Haversine km from the query lat/lng; null when geo not supplied. */
  readonly distanceKm: number | null;
  /**
   * F37 — Bayesian-weighted overall rating (1–5), or null when the trainer has
   * no reviews. Drives the `rating` + `recommended` sorts (D11).
   */
  readonly avgRating: number | null;
  /** F37 — overall published-review count (0 when no reviews). */
  readonly reviewCount: number;
  /** F37 — up to 3 most-frequent review tag codes for the card chip-cloud. */
  readonly topTags: readonly string[];
};

export type TrainerSearchResult = {
  readonly items: readonly TrainerSearchResultItem[];
  readonly nextCursor: string | null;
  readonly appliedSort: TrainerMarketplaceSort;
};
